import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError, getToken, fileUrl } from "@/lib/api";
import {
  ArrowLeft, Lock, Unlock, FileText, FileSpreadsheet, DollarSign,
  CheckCircle2, AlertTriangle, XCircle, Download, Loader2, Search, X, Fuel,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { businessDateStr } from "@/lib/businessDate";

const SIT_BADGE = {
  completo: { color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2, label: "Completo" },
  pendente: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Pendente" },
  nao_enviado: { color: "bg-red-100 text-red-700 border-red-200", icon: XCircle, label: "Não Enviado" },
};

function defaultPeriod() {
  const today = new Date();
  const d = today.getDate();
  let start, end;
  if (d >= 28) {
    start = new Date(today.getFullYear(), today.getMonth(), 28);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 27);
  } else {
    start = new Date(today.getFullYear(), today.getMonth() - 1, 28);
    end = new Date(today.getFullYear(), today.getMonth(), 27);
  }
  const iso = (x) => businessDateStr(x);
  return { start: iso(start), end: iso(end) };
}

function downloadFromApi(path, params, filename) {
  const token = getToken();
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params || {}).forEach(([k, v]) => v !== undefined && v !== "" && url.searchParams.set(k, v));
  return fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objUrl);
    });
}

export default function MonthlyClosing() {
  const def = defaultPeriod();
  const [periodStart, setPeriodStart] = useState(def.start);
  const [periodEnd, setPeriodEnd] = useState(def.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotaFilter, setRotaFilter] = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [regiaoFilter, setRegiaoFilter] = useState("");
  const [query, setQuery] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [busy, setBusy] = useState(false);
  const [driverDetail, setDriverDetail] = useState(null);
  const [openSheet, setOpenSheet] = useState(false);
  const [fuelData, setFuelData] = useState(null);


  async function load() {
    setLoading(true);
    try {
      const params = { period_start: periodStart, period_end: periodEnd };
      if (rotaFilter) params.rota = rotaFilter;
      if (supervisorFilter) params.supervisor = supervisorFilter;
      if (statusFilter !== "all") params.situacao = statusFilter;
      if (regiaoFilter) params.regiao = regiaoFilter;
      const { data } = await api.get("/monthly/summary", { params });
      setData(data);
      // fuel summary (no driver filter)
      try {
        const fr = await api.get("/monthly/fuel/summary", {
          params: { period_start: periodStart, period_end: periodEnd },
        });
        setFuelData(fr.data);
      } catch (e) { /* ignore */ }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periodStart, periodEnd, rotaFilter, supervisorFilter, statusFilter, regiaoFilter]);

  const rotaOptions = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.drivers.map((d) => d.rota).filter(Boolean))).sort();
  }, [data]);
  const supervisorOptions = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.drivers.map((d) => d.supervisor).filter(Boolean))).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.toLowerCase();
    return data.drivers.filter(
      (d) => !q || d.nome.toLowerCase().includes(q) || (d.matricula || "").toLowerCase().includes(q),
    );
  }, [data, query]);

  async function closeMonth() {
    setBusy(true);
    try {
      await api.post("/monthly/close", { period_start: periodStart, period_end: periodEnd, notes: "" });
      toast.success("Período fechado com sucesso");
      setConfirmClose(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function reopenMonth() {
    setBusy(true);
    try {
      await api.post("/monthly/reopen", { period_start: periodStart, period_end: periodEnd });
      toast.success("Período reaberto");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function exportFuel(format) {
    const params = { period_start: periodStart, period_end: periodEnd, format };
    const ext = format === "xlsx" ? "xlsx" : "pdf";
    try {
      await downloadFromApi("/monthly/fuel/export", params, `abastecimentos_${periodStart}_a_${periodEnd}.${ext}`);
      toast.success("Relatório gerado");
    } catch (e) {
      toast.error("Falha ao gerar relatório");
    }
  }

  async function exportConsolidated(format) {
    const params = { period_start: periodStart, period_end: periodEnd, format };
    if (rotaFilter) params.rota = rotaFilter;
    if (supervisorFilter) params.supervisor = supervisorFilter;
    if (statusFilter !== "all") params.situacao = statusFilter;
    if (regiaoFilter) params.regiao = regiaoFilter;
    const ext = format === "xlsx" ? "xlsx" : "pdf";
    try {
      await downloadFromApi("/monthly/export/consolidated", params, `fechamento_mensal_${periodStart}_a_${periodEnd}.${ext}`);
      toast.success("Relatório gerado");
    } catch (e) {
      toast.error("Falha ao gerar relatório");
    }
  }

  async function exportDriver(driver, format) {
    const params = { period_start: periodStart, period_end: periodEnd, format };
    const ext = format === "xlsx" ? "xlsx" : "pdf";
    const slug = driver.nome.replace(/[^a-zA-Z0-9]/g, "_");
    try {
      await downloadFromApi(`/monthly/export/driver/${driver.driver_id}`, params, `${format === "financial" ? "financeiro" : "fechamento"}_${slug}.${ext}`);
    } catch (e) {
      toast.error("Falha ao gerar arquivo");
    }
  }

  async function openDriverDetail(driver) {
    setDriverDetail({ loading: true, driver: { nome: driver.nome } });
    setOpenSheet(true);
    try {
      const { data } = await api.get(`/monthly/driver/${driver.driver_id}`, {
        params: { period_start: periodStart, period_end: periodEnd },
      });
      setDriverDetail({ loading: false, ...data });
    } catch (err) {
      toast.error(formatApiError(err));
      setOpenSheet(false);
    }
  }

  const ind = data?.indicators;
  const locked = data?.locked;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Período 28 → 27</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Fechamento Mensal</h1>
          {data && (
            <p className="mt-1 text-sm text-slate-500">
              {data.period_start_br} até {data.period_end_br}
              {locked && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-red-700">
                  <Lock className="h-3 w-3" /> Bloqueado
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => exportConsolidated("pdf")} className="bg-blue-900 hover:bg-blue-800" data-testid="month-generate-pdf">
            <FileText className="mr-1.5 h-4 w-4" /> Gerar Fechamento (PDF)
          </Button>
          <Button onClick={() => exportConsolidated("xlsx")} variant="outline" className="border-green-700 text-green-700 hover:bg-green-50" data-testid="month-export-xlsx">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Exportar Excel
          </Button>
          {locked ? (
            <Button onClick={reopenMonth} disabled={busy} variant="outline" className="border-orange-500 text-orange-700 hover:bg-orange-50" data-testid="month-reopen-btn">
              <Unlock className="mr-1.5 h-4 w-4" /> Reabrir Fechamento
            </Button>
          ) : (
            <Button onClick={() => setConfirmClose(true)} className="bg-red-700 text-white hover:bg-red-600" data-testid="month-close-btn">
              <Lock className="mr-1.5 h-4 w-4" /> Fechar Mês
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-7">
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Período - de</Label>
          <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} data-testid="month-period-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Período - até</Label>
          <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} data-testid="month-period-to" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Região</Label>
          <Select value={regiaoFilter || "all"} onValueChange={(v) => setRegiaoFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="month-filter-regiao"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="Guarulhos">Guarulhos</SelectItem>
              <SelectItem value="São Paulo">São Paulo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rota</Label>
          <Select value={rotaFilter || "all"} onValueChange={(v) => setRotaFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="month-filter-rota"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {rotaOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Supervisor</Label>
          <Select value={supervisorFilter || "all"} onValueChange={(v) => setSupervisorFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="month-filter-supervisor"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {supervisorOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Situação</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="month-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="completo">Completo</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="nao_enviado">Não Enviado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Motorista</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nome ou matrícula" className="pl-9" data-testid="month-filter-search" />
          </div>
        </div>
      </div>

      {/* Indicators */}
      {ind && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Fechamentos enviados</p>
            <p className="mt-1 font-display text-4xl font-bold text-slate-900" data-testid="kpi-enviados">
              {ind.fechamentos_enviados}<span className="text-2xl text-slate-400">/{ind.total_motoristas}</span>
            </p>
            <p className="mt-1 text-xs text-slate-500">{ind.finalizados} finalizados · {ind.pendentes} pendentes</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700">Motoristas pendentes</p>
            <p className="mt-1 font-display text-4xl font-bold text-amber-700" data-testid="kpi-pendentes">
              {ind.pendentes + ind.nao_enviados}
            </p>
            <p className="mt-1 text-xs text-slate-500">{ind.nao_enviados} ainda não enviaram</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-baseline justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Progresso mensal</p>
              <p className="font-display text-2xl font-bold text-blue-700" data-testid="kpi-progress">{ind.progress}%</p>
            </div>
            <Progress value={ind.progress} className="mt-3 h-3" />
            <p className="mt-2 text-xs text-slate-500">
              {ind.finalizados} de {ind.total_motoristas} motoristas finalizaram o período
            </p>
          </div>
        </div>
      )}

      {/* Drivers table */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Motorista</TableHead>
                <TableHead>Rota</TableHead>
                <TableHead>Supervisor</TableHead>
                <TableHead className="text-right">Dias</TableHead>
                <TableHead className="text-right">Entregues</TableHead>
                <TableHead className="text-right">Retornos</TableHead>
                <TableHead className="text-right">Fora</TableHead>
                <TableHead className="text-right">Devoluções</TableHead>
                <TableHead className="text-right">AR</TableHead>
                <TableHead className="text-right">OnLog</TableHead>
                <TableHead className="text-right">Coletas</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Sucesso</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={15} className="py-10 text-center text-slate-400">Carregando…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={15} className="py-10 text-center text-slate-400">Sem dados para o período.</TableCell></TableRow>
              )}
              {filtered.map((d) => {
                const sit = SIT_BADGE[d.situacao] || SIT_BADGE.pendente;
                const SitIcon = sit.icon;
                return (
                  <TableRow key={d.driver_id} data-testid={`month-row-${d.driver_id}`}>
                    <TableCell>
                      <button
                        className="font-medium text-slate-900 hover:text-blue-700 hover:underline"
                        onClick={() => openDriverDetail(d)}
                        data-testid={`month-open-detail-${d.driver_id}`}
                      >
                        {d.nome}
                      </button>
                      <div className="text-xs text-slate-500">{d.matricula}</div>
                    </TableCell>
                    <TableCell>{d.rota || "—"}</TableCell>
                    <TableCell>{d.supervisor || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{d.dias_trabalhados}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_entregues}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_retornos}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_fora}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_devolucoes || 0}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_ar || 0}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_onlog || 0}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_coletas || 0}</TableCell>
                    <TableCell className="text-right font-mono">{d.total_geral}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-blue-700">{d.sucesso}%</TableCell>
                    <TableCell>
                      <Badge className={`gap-1 border ${sit.color}`}>
                        <SitIcon className="h-3 w-3" /> {sit.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => exportDriver(d, "pdf")}
                          className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition hover:border-blue-700 hover:text-blue-700"
                          title="Extrair PDF"
                          data-testid={`month-extract-pdf-${d.driver_id}`}
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => exportDriver(d, "xlsx")}
                          className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition hover:border-green-700 hover:text-green-700"
                          title="Extrair Excel"
                          data-testid={`month-extract-xlsx-${d.driver_id}`}
                        >
                          <FileSpreadsheet className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => exportDriver(d, "financial")}
                          className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition hover:border-amber-700 hover:text-amber-700"
                          title="Resumo Financeiro"
                          data-testid={`month-extract-fin-${d.driver_id}`}
                        >
                          <DollarSign className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Fuel summary */}
      {fuelData && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40" data-testid="fuel-summary-section">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-700">
                <Fuel className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-800">Cartão de Abastecimento</p>
                <h2 className="font-display text-lg font-semibold text-slate-900">Resumo de Abastecimentos</h2>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => exportFuel("pdf")} size="sm" className="bg-amber-700 hover:bg-amber-600 text-white" data-testid="fuel-export-pdf">
                <FileText className="mr-1.5 h-4 w-4" /> Exportar Relatório (PDF)
              </Button>
              <Button onClick={() => exportFuel("xlsx")} size="sm" variant="outline" className="border-green-700 text-green-700 hover:bg-green-50" data-testid="fuel-export-xlsx">
                <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-3">
            <FuelStat label="Quantidade" value={fuelData.count} testId="fuel-count" />
            <FuelStat label="Valor Total" value={`R$ ${fuelData.total_valor.toFixed(2).replace(".", ",")}`} accent="text-amber-700" testId="fuel-total" />
            <FuelStat label="Média por abastecimento" value={`R$ ${fuelData.media.toFixed(2).replace(".", ",")}`} testId="fuel-media" />
          </div>
          {fuelData.items.length > 0 && (
            <div className="overflow-x-auto border-t border-amber-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Motorista</TableHead>
                    <TableHead>Rota</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Comprovante</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fuelData.items.slice(0, 30).map((it) => (
                    <TableRow key={it.closing_id} data-testid={`fuel-item-${it.closing_id}`}>
                      <TableCell className="text-slate-600">
                        {it.abastecimento_data ? new Date(it.abastecimento_data + "T00:00:00").toLocaleDateString("pt-BR") : "—"}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">{it.driver_nome}</TableCell>
                      <TableCell>{it.rota || "—"}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-amber-700">
                        R$ {it.valor.toFixed(2).replace(".", ",")}
                      </TableCell>
                      <TableCell>
                        {it.comprovante ? (
                          <a
                            href={fileUrl(it.comprovante.storage_path)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
                          >
                            <FileText className="h-3.5 w-3.5" /> Abrir
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {fuelData.items.length > 30 && (
                <p className="border-t border-amber-200 px-4 py-2 text-xs text-slate-500">
                  Exibindo 30 de {fuelData.items.length} · exporte para ver todos
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirm close dialog */}
      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Fechar período?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Deseja realmente fechar o período <strong>{data?.period_start_br} até {data?.period_end_br}</strong>?
            Após o fechamento, motoristas não poderão mais editar fechamentos deste período. Todos os relatórios permanecerão salvos.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClose(false)}>Cancelar</Button>
            <Button onClick={closeMonth} disabled={busy} className="bg-red-700 hover:bg-red-600" data-testid="month-confirm-close-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-1.5 h-4 w-4" /> Confirmar fechamento</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver detail sheet */}
      <Sheet open={openSheet} onOpenChange={setOpenSheet}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{driverDetail?.driver?.nome || "Motorista"}</SheetTitle>
          </SheetHeader>
          {driverDetail?.loading ? (
            <p className="mt-6 text-sm text-slate-500">Carregando…</p>
          ) : driverDetail ? (
            <DriverDetailBody detail={driverDetail} onExtract={exportDriver} />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FuelStat({ label, value, accent = "text-slate-900", testId }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-white p-4" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

function DriverDetailBody({ detail, onExtract }) {
  const { driver, summary, days } = detail;
  if (!summary) return <p className="mt-6 text-sm text-slate-500">Sem dados no período.</p>;

  return (
    <div className="mt-6 space-y-6">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Resumo do Período</p>
        <h3 className="mt-1 font-display text-xl font-bold text-slate-900">{driver.nome}</h3>
        <p className="text-xs text-slate-500">{summary.period_start_br} a {summary.period_end_br}</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <K label="Dias Trabalhados" value={summary.dias_trabalhados} />
          <K label="Sem Fechamento" value={summary.dias_sem_fechamento} accent="text-amber-700" />
          <K label="Sucesso" value={`${summary.sucesso.toFixed(2)}%`} accent="text-blue-700" />
          <K label="Entregues" value={summary.total_entregues} accent="text-green-700" />
          <K label="Retornos" value={summary.total_retornos} accent="text-amber-700" />
          <K label="Fora de Lista" value={summary.total_fora} accent="text-red-700" />
          <K label="Devoluções" value={summary.total_devolucoes} accent="text-violet-700" />
          <K label="Remessas AR" value={summary.total_ar} accent="text-blue-700" />
          <K label="OnLog" value={summary.total_onlog} accent="text-cyan-700" />
          <K label="Total Geral" value={summary.total_geral} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onExtract({ driver_id: driver.id, nome: driver.nome }, "pdf")} className="bg-blue-900 hover:bg-blue-800" data-testid="detail-extract-pdf">
          <FileText className="mr-1.5 h-4 w-4" /> Extrair PDF
        </Button>
        <Button onClick={() => onExtract({ driver_id: driver.id, nome: driver.nome }, "xlsx")} variant="outline" className="border-green-700 text-green-700 hover:bg-green-50" data-testid="detail-extract-xlsx">
          <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Extrair Excel
        </Button>
        <Button onClick={() => onExtract({ driver_id: driver.id, nome: driver.nome }, "financial")} variant="outline" className="border-amber-700 text-amber-700 hover:bg-amber-50" data-testid="detail-extract-fin">
          <DollarSign className="mr-1.5 h-4 w-4" /> Resumo Financeiro
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Detalhamento diário</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Entregues</TableHead>
                <TableHead className="text-right">Retornos</TableHead>
                <TableHead className="text-right">Fora</TableHead>
                <TableHead className="text-right">Devoluções</TableHead>
                <TableHead className="text-right">AR</TableHead>
                <TableHead className="text-right">OnLog</TableHead>
                <TableHead className="text-right">Coletas</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Conferido por</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {days.map((d) => (
                <TableRow key={d.date} className={d.status === "sem_envio" ? "bg-slate-50/60" : ""}>
                  <TableCell className={d.weekday >= 5 ? "text-slate-400" : ""}>
                    {d.date_br}
                  </TableCell>
                  <TableCell className="text-right font-mono">{d.entregues ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{d.retornos ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{d.fora_lista_qtd ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{d.devolucao_qtd ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{d.remessas_ar_qtd ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{d.onlog_qtd ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{d.coletas_qtd ?? "—"}</TableCell>
                  <TableCell>
                    {d.status === "sem_envio" ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <Badge variant="secondary" className="font-normal">{d.status}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500">{d.reviewer_nome || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function K({ label, value, accent = "text-slate-900" }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-xl font-bold ${accent}`}>{value ?? "—"}</p>
    </div>
  );
}
